/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import {
    totalToIndexVisitor,
    getMeasures,
    getTooltipValue,
    stripChildrenForIndexes
} from './util/GenericSeriesUtil';
import GenericChartVisualization from './GenericChartVisualization';
import {
    IMultiAxisDatasetNode,
    IMultiAxisDatasetVisitor
} from '../../../store/types/MultiAxisDatasetTypes';
import visitMultiAxisDatasetNode from '../../../store/util/visitMultiAxisDatasetNode';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from './util/EventUtil';

type IColumnSeriesVisitorContextArray = {
    indexes: number[],
    labels: string[]
}[];

interface IColumnSeriesVisitorContext {
    array: IColumnSeriesVisitorContextArray,
    labelToArrayIndex: {
        [label: string]: number
    }
    counter: number,
    stack: Array<IMultiAxisDatasetNode>,
    measures: any,
    selectedColIndex: number,
    inMeasures: boolean
}

type IRowsToArrayVisitorContextRows = {
    index: number,
    labels: string[]
}[];

interface IRowsToArrayVisitorContext {
    stack: IMultiAxisDatasetNode[],
    rows: IRowsToArrayVisitorContextRows,
    selectedRowIndex: number
}

const generateLabelsFromMembersStack = (stack: IMultiAxisDatasetNode[]) => {
    const labels :string[] = [];
    stack.forEach((nodeFromStack) => {
        if (nodeFromStack.label && !nodeFromStack.isMeasure) {
            labels.push(nodeFromStack.label);
        }
    });
    return labels.reverse();
};

const pointFormat = (prefix: string, measures: any) => {

    const lines = [];
    if (prefix) {
        lines.push(prefix);
    }

    lines.push(`<span style="color:{point.color}">\u25CF</span> ${measures[0].label}: <b>{point.xTooltipValue}</b>`);
    lines.push(`<span style="color:{point.color}">\u25CF</span> ${measures[1].label}: <b>{point.yTooltipValue}</b>`);

    if (measures[2]) {
        lines.push(`<span style="color:{point.color}">\u25CF</span> ${measures[2].label}: <b>{point.zTooltipValue}</b>`);
    }

    return lines.join('<br/>');
};

const createOrUpdateSeriesItem = (context: IColumnSeriesVisitorContext, labels: string[], index: number) => {
    const newContext = { ...context, array: [...context.array] };

    const label = labels.join(', ');

    const existingSeriesIndex = newContext.labelToArrayIndex[label];

    if (typeof existingSeriesIndex !== 'undefined') {
        const existingSeries = newContext.array[existingSeriesIndex];
        existingSeries.indexes[existingSeries.indexes.length] = index;
    } else {

        newContext.array.push({
            indexes: [index],
            labels
        });

        newContext.labelToArrayIndex[label] = newContext.array.length - 1;
        newContext.counter += 1;
    }

    return newContext;
};

const columnSeriesVisitor: IMultiAxisDatasetVisitor<IColumnSeriesVisitorContext> = {
    preVisitNode(node, ctxt) {
        const context = ctxt;
        context.stack.push(node);

        if (!context.inMeasures && node.isMeasure) {
            context.inMeasures = true;
        }

        return context;
    },

    postVisitNode(node, ctxt, currentDepth) {
        let context: IColumnSeriesVisitorContext = { ...ctxt };

        if (currentDepth === context.selectedColIndex && !node.isTotal) {
            const { array: indexes } = visitMultiAxisDatasetNode(
                stripChildrenForIndexes(node),
                totalToIndexVisitor,
                { array: [] }
            );

            const labels = generateLabelsFromMembersStack(context.stack);
            const { measures } = context;

            if (context.inMeasures || !measures) {
                context = createOrUpdateSeriesItem(context, labels, indexes[0]);
            } else {
                context = measures.reduce((memoContext: IColumnSeriesVisitorContext, measure: any, mIndex: number) => {
                    return createOrUpdateSeriesItem(memoContext, labels, indexes[mIndex]);
                }, context);
            }
        }

        context.stack.pop();

        if (context.inMeasures && node.isMeasure) {
            context.inMeasures = false;
        }

        return context;
    }
};

const rowsToArrayVisitor: IMultiAxisDatasetVisitor<IRowsToArrayVisitorContext> = {
    preVisitNode(node, ctxt) {
        const context = ctxt;
        context.stack.push(node);

        return context;
    },

    postVisitNode(node: IMultiAxisDatasetNode, ctxt: IRowsToArrayVisitorContext, currentDepth: number) {
        let context = { ...ctxt };

        if (currentDepth === context.selectedRowIndex && !node.isTotal) {
            const { array: indexes } = visitMultiAxisDatasetNode(
                stripChildrenForIndexes(node),
                totalToIndexVisitor,
                { array: [] }
            );

            const labels = generateLabelsFromMembersStack(context.stack);

            context = {
                ...ctxt,
                rows: [...context.rows, {
                    index: indexes[0],
                    labels
                }]
            }
        }

        context.stack.pop();

        return context;
    }
};

export default class ScatterAndBubbleGenericChartVisualization extends GenericChartVisualization {

    getLegendSection() {
        const section = super.getLegendSection();
        section.enabled = this.getSelectedColumnIndex() > 0;
        return section;
    }

    getTooltipSection() {
        const tooltip = super.getTooltipSection();

        return {
            ...tooltip,
            followPointer: true,
            shared: false
        }
    }

    getPlotOptionsSection(): any {
        const plotOptions = super.getPlotOptionsSection();

        // remove tooltip option from the series options generated by parent class
        delete plotOptions.series.tooltip;

        return plotOptions;
    }

    getXAxisSection(selectedRowIndex: number) {
        const xAxis = super.getXAxisSection(selectedRowIndex);
        const measures = getMeasures(this.getStaticProperties().levelsStructure);

        return {
            ...xAxis,
            title: {
                text: measures && measures[0].label
            }
        }
    }

    getYAxisSection() {
        const yAxis = super.getYAxisSection();

        const measures = getMeasures(this.getStaticProperties().levelsStructure);

        return {
            ...yAxis,
            title: {
                text: measures && measures[1] && measures[1].label
            }
        };
    }

    getColorWithAlpha(index :number, alpha :number) {
        const colorsToUse = this.getColorsToUse();
        let hexColor = colorsToUse[index % colorsToUse.length];

        hexColor = hexColor.replace('#', '');

        const r = parseInt(hexColor.substring(0, 2), 16);
        const g = parseInt(hexColor.substring(2, 4), 16);
        const b = parseInt(hexColor.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    seriesBuilder() {

        const dataset = this.getDataset();
        const linkOptions :ILinkOptions = this.getLinkOptions();
        const measures = getMeasures(this.getStaticProperties().levelsStructure);
        const selectedColIndex = this.getSelectedColumnIndex();
        const selectedRowIndex = this.getSelectedRowIndex();

        const rowItems :any[] = dataset.summary.rows.items;
        const columnItems :any[] = dataset.summary.columns.items;
        const fieldsInRows :any[] = [];
        const fieldsInColumns :any[] = [];
        rowItems.forEach((item :any, index :number) => {
            if (item.measuresLevel === false) fieldsInRows.push({ ...item, position: index })
        });
        columnItems.forEach((item :any, index :number) => {
            if (item.measuresLevel === false) fieldsInColumns.push({ ...item, position: index })
        });

        const columnSeries = visitMultiAxisDatasetNode(dataset.data.columns, columnSeriesVisitor, {
            array: [],
            labelToArrayIndex: {},
            stack: [],
            counter: 0,
            inMeasures: false,
            measures,
            selectedColIndex
        }).array;

        const rowsIndexes = visitMultiAxisDatasetNode(dataset.data.rows, rowsToArrayVisitor, {
            stack: [],
            rows: [],
            selectedRowIndex
        });

        const getTooltipProps = {
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        return columnSeries.map((column, columnIndex) => {

            const data :Array<{ y: any, x: any, z?: any, name: string }> = [];

            rowsIndexes.rows.forEach((row) => {

                const axesValues = column.indexes.map((value) => {
                    const aggregationValue = dataset.data.aggregations[row.index][value];
                    return typeof aggregationValue === 'string' ? parseFloat(aggregationValue) : aggregationValue;
                });

                const isAllPositive = !axesValues.some((value) => {
                    return value <= 0;
                });

                if (!isAllPositive) {
                    return;
                }

                const [x, y, z] = axesValues;
                let rowLabel = [...row.labels].reverse().join(', ');
                if (rowLabel === '') {
                    rowLabel = ' ';
                }

                const xTooltipValue = getTooltipValue(getTooltipProps, x, measures[0]);
                const yTooltipValue = getTooltipValue(getTooltipProps, y, measures[1]);

                const dataItem :any = {
                    x,
                    xTooltipValue,
                    y,
                    yTooltipValue,
                    name: rowLabel
                };

                if (column.indexes.length === 3) {
                    const zTooltipValue = getTooltipValue(getTooltipProps, z, measures[2]);
                    dataItem.z = z;
                    dataItem.zTooltipValue = zTooltipValue;
                }

                if (linkOptions) {
                    const linkOutputParams: any = {
                        measures: measures.map((measure :any) => {
                            return getOutputParamFromMeasure(measure);
                        }),
                    };

                    const columnValues = fieldsInColumns.map((item: any, fieldIndex: number) => {
                        return {
                            key: getOutputParamsKeyFromField(item),
                            value: column.labels[column.labels.length - (fieldIndex + 1)] || ''
                        }
                    });
                    const rowValues = fieldsInRows.map((item: any, fieldIndex: number) => {
                        return {
                            key: getOutputParamsKeyFromField(item),
                            value: row.labels[row.labels.length - (fieldIndex + 1)] || ''
                        }
                    });
                    linkOutputParams.values = columnValues.concat(rowValues);
                    dataItem.linkOutputParams = linkOutputParams;
                }

                data.push(dataItem);
            });

            let headerFormat = '';
            if (fieldsInRows.length && selectedRowIndex > 0) {
                headerFormat = '{point.key}<br/>';
            }
            let pointFormatPrefix = '';
            if (fieldsInColumns.length) {
                pointFormatPrefix = '<span style="color:{series.color}">{series.name}</span>';
            }

            const serie :any = {
                color: this.getColorWithAlpha(columnIndex, 0.75),
                name: column.labels.join(', '),
                data,
                yAxis: 0,
                tooltip: {
                    headerFormat,
                    pointFormat: pointFormat(pointFormatPrefix, measures),
                    followPointer: true
                }
            };

            if (linkOptions) {
                serie.cursor = 'pointer';
                serie.point = {
                    events: getEvents(linkOptions)
                };
            }

            return serie;
        });
    }

    getSeriesSection(): Highcharts.IndividualSeriesOptions[] {

        const formattingOptions = this.getFormattingOptions();

        const series = this.seriesBuilder();

        if (formattingOptions.showScatterLine) {
            series.forEach((serie: any) => {
                // eslint-disable-next-line no-param-reassign
                serie.lineWidth = 1;
            });
        }

        let firstMinMaxRecorded :boolean = false,
            min :number = 0,
            max :number = 0;
        for (let i = 0; i < series.length; i += 1) {
            const serie = series[i];
            for (let k = 0; k < serie.data.length; k += 1) {
                const value = serie.data[k].y;
                if (!firstMinMaxRecorded) {
                    min = value;
                    max = value;
                    firstMinMaxRecorded = true;
                } else {
                    min = Math.min(min, value);
                    max = Math.max(max, value);
                }
            }
        }
        this.recordMinMaxValues(min, max);

        return series;
    }
}
